import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, Drawer, IconButton, InputBase, Paper, Popper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { color, r, shadow } from '../../theme/tokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import useSoftwareKeyboard from '../../hooks/useSoftwareKeyboard';
import { ALL_GOALS, GOAL_GROUPS } from './curatedHealthGoals';
import { applyPopoverScroll, planBelow } from './popoverPlacement';

// The goal box as a combobox (Aravind, 2026-10-09, STACK-565; supersedes the
// Health goals button and panel): one field, one answer. The WAI-ARIA
// combobox pattern with a grouped listbox —
//   - focus, or the chevron at the field's right edge, opens every preset goal
//     grouped by area; typing filters it (case-insensitive substring) and keeps
//     the text; a custom goal gets a last row, Use "<text>", that only closes
//     the list;
//   - Up/Down move the active row (aria-activedescendant), Enter picks it,
//     Escape closes; a pick fills the exact preset label, which generation's
//     rate-limit exemption matches on, and never submits;
//   - desktop: a dropdown under the field, at its width, height capped at
//     23rem, scrolling inside. It always opens below — it must not cover the
//     text it suggests for — using the room there whenever about four goals
//     fit, and scrolls the page only when fewer would (round 7, `planBelow`).
//     Phone: the same, unless the keyboard leaves less
//     than 12rem below the field — then a bottom sheet with the input pinned at
//     the top, the same mechanics as the Medications panel.
//
// `useGoalCombobox` holds the state and keyboard handling; StackComposer
// spreads its attributes onto the goal input and renders `<GoalSuggestions>`.

const LIST_MAX_PX = 368; // 23rem
const SHEET_BELOW_PX = 192; // 12rem: less room than this below the field → sheet
const LIST_MIN_PX = 176; // 11rem: a group label and four goals
const POPPER_GAP_PX = 8;
const EDGE_MARGIN_PX = 16;

const lower = (s) => s.trim().toLowerCase();

/** The preset `text` names, ignoring case and surrounding space, or null. */
export const matchPreset = (text) => {
  const t = lower(text || '');
  return t ? ALL_GOALS.find((goal) => goal.toLowerCase() === t) || null : null;
};

/** The groups, filtered by a case-insensitive substring; empty groups drop. */
export const filterGoalGroups = (query) => {
  const q = lower(query || '');
  return GOAL_GROUPS
    .map(({ area, goals }) => ({ area, goals: goals.filter((goal) => goal.toLowerCase().includes(q)) }))
    .filter(({ goals }) => goals.length);
};

export function useGoalCombobox({ enabled, value, setValue, surface, compact, anchorRef, inputRef, onPick }) {
  const listId = `goal-suggestions-${useId().replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('popover');
  const [activeIndex, setActiveIndex] = useState(-1);
  // Filtering turns on when the visitor types. Reopening keeps it when the
  // field still holds a partial / custom query; an exact preset (or empty)
  // shows every goal again so they can pick a different one.
  const [filtering, setFiltering] = useState(false);
  const [maxHeightPx, setMaxHeightPx] = useState(LIST_MAX_PX);
  const [placement, setPlacement] = useState('bottom-start');
  const [anchorWidthPx, setAnchorWidthPx] = useState(null);
  // composer_goals_opened fires at most once per focus.
  const loggedThisFocusRef = useRef(false);
  const chevronFocusRef = useRef(false);
  // Focus moving from the composer's field into the sheet's is not a blur.
  const movingToSheetRef = useRef(false);

  const groups = filterGoalGroups(filtering ? value : '');
  const trimmed = (value || '').trim();
  const custom = trimmed && !matchPreset(trimmed) ? trimmed : null;
  const options = [
    ...groups.flatMap(({ goals }) => goals.map((goal) => ({ kind: 'goal', goal }))),
    ...(custom ? [{ kind: 'custom', text: custom }] : []),
  ];
  const optionId = (index) => `${listId}-option-${index}`;

  // Phone: room below the field in the visible viewport (the keyboard
  // shrinks it); the list fills it, or the sheet takes over.
  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return Infinity;
    const rect = el.getBoundingClientRect();
    if (rect.width) setAnchorWidthPx(rect.width);
    if (!compact) return Infinity;
    const vv = window.visualViewport;
    const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const room = visibleBottom - rect.bottom - POPPER_GAP_PX - EDGE_MARGIN_PX;
    setMaxHeightPx(Math.min(LIST_MAX_PX, Math.max(0, room)));
    setPlacement('bottom-start');
    return room;
  }, [anchorRef, compact]);

  // Desktop: below the card in the room there, scrolling only when fewer than
  // about four goals would fit, once per open.
  const planDesktop = () => {
    const el = anchorRef.current;
    if (compact || !el) return;
    const plan = planBelow(el, { minPx: LIST_MIN_PX, capPx: LIST_MAX_PX });
    applyPopoverScroll(plan);
    setPlacement(plan.placement);
    setMaxHeightPx(plan.maxHeightPx);
  };

  const wantsSheet = (room) => compact && room < SHEET_BELOW_PX;

  const openList = (trigger) => {
    if (!enabled) return;
    const room = measure();
    if (wantsSheet(room)) {
      movingToSheetRef.current = true;
      setMode('sheet');
    } else {
      setMode('popover');
      planDesktop();
    }
    setOpen(true);
    setActiveIndex(-1);
    const text = (value || '').trim();
    setFiltering(Boolean(text) && !matchPreset(text));
    if (!loggedThisFocusRef.current) {
      loggedThisFocusRef.current = true;
      logAnalyticsEvent('composer_goals_opened', { surface, trigger });
    }
  };

  // Closing the sheet ends the focus session too: its input unmounts, and
  // focus is not handed back to the composer's field (which would reopen it).
  const closeList = () => {
    setOpen(false);
    setActiveIndex(-1);
    setFiltering(false);
    if (mode === 'sheet') {
      movingToSheetRef.current = false;
      loggedThisFocusRef.current = false;
      setMode('popover');
    }
  };

  // While the popover is open on a phone, the keyboard arriving can leave too
  // little room below the field: move to the sheet then.
  useEffect(() => {
    if (!open || mode !== 'popover') return undefined;
    const onResize = () => {
      const room = measure();
      if (wantsSheet(room)) {
        movingToSheetRef.current = true;
        setMode('sheet');
      }
    };
    window.visualViewport?.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
    };
  }); // re-bound each render: `measure` and `compact` are read fresh

  // Keep the active row in view as the arrows move it.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  });

  const choose = (option) => {
    if (option.kind === 'goal') {
      setValue(option.goal);
      onPick?.(option.goal);
    }
    closeList();
  };

  // Returns true when the combobox used the key, so the composer does not
  // also act on it (Enter submits only with the list closed).
  const onKeyDown = (e) => {
    if (!enabled) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openList('focus');
      else if (options.length) setActiveIndex((i) => (i + 1) % options.length);
      return true;
    }
    if (e.key === 'ArrowUp') {
      if (!open) return false;
      e.preventDefault();
      if (options.length) setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
      return true;
    }
    if (e.key === 'Enter' && !e.shiftKey && open) {
      e.preventDefault();
      if (activeIndex >= 0 && options[activeIndex]) choose(options[activeIndex]);
      else closeList();
      return true;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      closeList();
      return true;
    }
    if (e.key === 'Tab' && open) closeList();
    return false;
  };

  const onInputChange = () => {
    if (!enabled) return;
    setFiltering(true);
    setActiveIndex(-1);
    if (!open) {
      openList('focus');
      setFiltering(true);
    }
  };

  const onFocus = () => {
    if (!enabled || open) return;
    const trigger = chevronFocusRef.current ? 'chevron' : 'focus';
    chevronFocusRef.current = false;
    openList(trigger);
  };

  const onBlur = () => {
    if (!enabled) return;
    if (movingToSheetRef.current) return;
    closeList();
    loggedThisFocusRef.current = false;
  };

  const onSheetClose = () => closeList();

  const onChevronClick = () => {
    if (open) {
      closeList();
      return;
    }
    if (document.activeElement === inputRef.current) {
      openList('chevron');
    } else {
      chevronFocusRef.current = true;
      inputRef.current?.focus();
    }
  };

  const inputAttributes = enabled ? {
    role: 'combobox',
    'aria-expanded': open,
    'aria-controls': listId,
    'aria-autocomplete': 'list',
    'aria-activedescendant': open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
  } : {};

  return {
    enabled,
    open,
    mode,
    listId,
    groups,
    custom,
    options,
    optionId,
    activeIndex,
    setActiveIndex,
    maxHeightPx,
    placement,
    anchorWidthPx,
    selected: matchPreset(value),
    value,
    inputAttributes,
    onKeyDown,
    onInputChange,
    onFocus,
    onBlur,
    onChevronClick,
    onSheetClose,
    choose,
  };
}

/** The chevron at the field's right edge. Not a tab stop: Down opens the list. */
export function GoalChevron({ combo, disabled }) {
  if (!combo.enabled) return null;
  return (
    <IconButton
      tabIndex={-1}
      aria-label="Common goals"
      aria-expanded={combo.open}
      aria-controls={combo.listId}
      disabled={disabled}
      // Keep focus in the field, so the list stays a combobox popup.
      onMouseDown={(e) => e.preventDefault()}
      onClick={combo.onChevronClick}
      data-testid="composer-goals-chevron"
      sx={{
        flex: 'none',
        alignSelf: 'center',
        // Phone targets stay ≥44px (STACK-565 sighted pass); desktop can be denser.
        width: { xs: r(44), sm: r(32) },
        height: { xs: r(44), sm: r(32) },
        color: color.inkSoft,
        visibility: disabled ? 'hidden' : 'visible',
      }}
    >
      <ExpandMoreIcon
        sx={{
          fontSize: r(22),
          transform: combo.open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s ease',
        }}
      />
    </IconButton>
  );
}

function SuggestionList({ combo, labelledBy }) {
  let index = -1;
  const row = (option, label, isSelected) => {
    index += 1;
    const i = index;
    const active = i === combo.activeIndex;
    return (
      <Box
        key={option.kind === 'goal' ? option.goal : `custom-${option.text}`}
        id={combo.optionId(i)}
        role="option"
        aria-selected={active}
        onClick={() => combo.choose(option)}
        onMouseMove={() => { if (!active) combo.setActiveIndex(i); }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: r(8),
          minHeight: { xs: r(44), sm: r(36) },
          px: r(16),
          cursor: 'pointer',
          fontSize: { xs: r(15), sm: r(14) },
          fontWeight: isSelected ? 700 : 500,
          color: isSelected ? color.evergreen : color.ink,
          backgroundColor: active ? color.mint : 'transparent',
        }}
      >
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</Box>
        {isSelected && <CheckRoundedIcon sx={{ fontSize: r(18), color: color.evergreen }} aria-hidden />}
      </Box>
    );
  };

  return (
    <Box
      id={combo.listId}
      role="listbox"
      aria-label={labelledBy ? undefined : 'Common goals'}
      aria-labelledby={labelledBy}
      data-testid="composer-goal-suggestions"
      sx={{ py: r(4) }}
    >
      {combo.groups.map(({ area, goals }, g) => (
        <Box key={area} role="group" aria-labelledby={`${combo.listId}-group-${g}`}>
          <Typography
            id={`${combo.listId}-group-${g}`}
            role="presentation"
            sx={{
              fontSize: r(11),
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: color.inkSoft,
              px: r(16),
              pt: r(10),
              pb: r(4),
              m: 0,
            }}
          >
            {area}
          </Typography>
          {goals.map((goal) => row({ kind: 'goal', goal }, goal, goal === combo.selected))}
        </Box>
      ))}
      {combo.custom && row({ kind: 'custom', text: combo.custom }, `Use “${combo.custom}”`, false)}
    </Box>
  );
}

/**
 * The popup: a dropdown under the field, or, on a phone with the keyboard
 * leaving too little room, a bottom sheet with its own pinned input.
 */
export function GoalSuggestions({ combo, anchorRef, onSheetInputChange }) {
  const theme = useTheme();
  const keyboardOpen = useSoftwareKeyboard();
  const sheetInputRef = useRef(null);
  if (!combo.enabled) return null;

  if (combo.mode === 'sheet') {
    return (
      <Drawer
        anchor="bottom"
        open={combo.open}
        onClose={combo.onSheetClose}
        disableRestoreFocus
        // Above the app's bottom navigation, as the Medications sheet is.
        sx={{ zIndex: theme.zIndex.modal + 1 }}
        PaperProps={{
          'data-testid': 'composer-goal-sheet',
          sx: {
            borderTopLeftRadius: r(20),
            borderTopRightRadius: r(20),
            height: combo.open && keyboardOpen ? '100dvh' : '65vh',
            transition: 'height 0.2s ease',
            boxShadow: shadow.modal,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box aria-hidden sx={{ display: 'flex', justifyContent: 'center', pt: r(8) }}>
          <Box sx={{ width: r(36), height: r(4), borderRadius: r(2), backgroundColor: color.line }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: r(8), pt: r(8), pr: r(8), pb: r(8), pl: r(16), borderBottom: `${r(1)} solid ${color.line}` }}>
          <InputBase
            inputRef={sheetInputRef}
            autoFocus
            fullWidth
            value={combo.value}
            // The composer's own change handler, which also filters the list.
            onChange={(e) => onSheetInputChange(e.target.value)}
            onKeyDown={combo.onKeyDown}
            placeholder="Type your goal"
            inputProps={{
              'aria-label': 'Describe your health goal',
              ...combo.inputAttributes,
            }}
            sx={{ fontSize: r(16), color: color.ink }}
          />
          <IconButton aria-label="Close" onClick={combo.onSheetClose} sx={{ width: r(44), height: r(44), color: color.inkSoft }}>
            <CloseRoundedIcon sx={{ fontSize: r(20) }} />
          </IconButton>
        </Box>
        <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }} onMouseDown={(e) => e.preventDefault()}>
          <SuggestionList combo={combo} />
        </Box>
      </Drawer>
    );
  }

  return (
    <Popper
      open={combo.open}
      anchorEl={anchorRef.current}
      placement={combo.placement}
      // Popper's own flip stays off (flipped above, it covered the hero);
      // `planBelow` keeps it under the card.
      modifiers={[
        { name: 'offset', options: { offset: [0, POPPER_GAP_PX] } },
        { name: 'flip', enabled: false },
      ]}
      sx={{
        zIndex: theme.zIndex.modal,
        width: combo.anchorWidthPx ? `${combo.anchorWidthPx / 16}rem` : '100%',
        maxWidth: 'calc(100vw - 2rem)',
      }}
    >
      <Paper
        elevation={0}
        // Keeps focus in the field while a row is clicked or the list scrolled.
        onMouseDown={(e) => e.preventDefault()}
        sx={{
          border: `${r(1)} solid ${color.line}`,
          borderRadius: r(16),
          boxShadow: shadow.pop,
          maxHeight: `${combo.maxHeightPx / 16}rem`,
          overflowY: 'auto',
        }}
      >
        <SuggestionList combo={combo} />
      </Paper>
    </Popper>
  );
}
